import * as React from "react";
import {
	ActionTypes,
	Action,
	State,
	Dispatch,
	TaxonomiesProviderProps,
} from "./types";

const TaxonomiesStateContext = React.createContext<
	{ state: State; dispatch: Dispatch } | undefined
>(undefined);

function taxonomiesReducer(state: State, action: Action) {
	switch (action.type) {
		case ActionTypes.HANDLE_FILE_CHECKED_SUCCESSFULLY: {
			return {
				...state,
				isFileChecked: true,
				fileName: action.payload.fileName,
				errorMessages: [],
				errors: [],
				data: action.payload.data,
				loading: false,
			};
		}
		case ActionTypes.FILE_ERRORS: {
			return {
				...state,
				isFileChecked: false,
				fileName: action.payload.fileName,
				errorMessages: action.payload.errorMessages || [],
				errors: [...action.payload.validations],
				loading: false,
			};
		}
		case ActionTypes.SET_COURSE_DETAILS: {
			return {
				...state,
				providers: [...action.payload.providers],
				categories: [...action.payload.categories],
				ageGroups: [...action.payload.ageGroups],
				selectedProviders: [...action.payload.selectedProviders],
				selectedCategory: action.payload.selectedCategory,
				selectedAgeGroups: [...action.payload.selectedAgeGroups],
				isSettingAgeGroups: action.payload.isSettingAgeGroups,
				isDeci: action.payload.isDeci,
				selectedSemester: action.payload.selectedSemester,
			};
		}
		case ActionTypes.SET_PRODUCTION_ID: {
			const dataWithProductionId = state.data.map((item, index) => ({
				...item,
				productionId: action.payload.data[index].id,
			}));
			return {
				...state,
				data: dataWithProductionId,
			};
		}
		case ActionTypes.SET_COURSES_TYPE: {
			return {
				...state,
				courseType: action.payload.courseType,
			};
		}
		case ActionTypes.SET_UPLOAD_MODE: {
			return {
				...state,
				uploadMode: action.payload.uploadMode,
				selectedCourses: [],
				targetCourses: {},
			};
		}
		case ActionTypes.SET_SELECTED_COURSES: {
			return {
				...state,
				selectedCourses: [...action.payload.selectedCourses],
			};
		}
		case ActionTypes.SET_TARGET_COURSES: {
			return {
				...state,
				targetCourses: action.payload.targetCourses,
			};
		}
		case ActionTypes.HANDLE_LOADING: {
			return {
				...state,
				loading: action.payload.loading,
			};
		}
		case ActionTypes.RESET_FILE: {
			// Keeps the upload mode and the course type the admin picked.
			return {
				...state,
				isFileChecked: false,
				fileName: "",
				errorMessages: [],
				errors: [],
				data: [],
				loading: false,
			};
		}
		case ActionTypes.RESET: {
			return {
				...state,
				isFileChecked: false,
				fileName: "",
				errorMessages: [],
				errors: [],
				data: [],
				providers: [],
				categories: [],
				selectedProviders: [],
				selectedCategory: "",
				courseType: "",
				selectedSemester: "",
				uploadMode: "NEW" as const,
				selectedCourses: [],
				targetCourses: {},
				loading: false,
			};
		}
		default: {
			// TODO: still enhancing the types here
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			throw new Error(`Unhandled action type: ${action.type}`);
		}
	}
}

function TaxonomiesProvider({ children }: TaxonomiesProviderProps) {
	const [state, dispatch] = React.useReducer(taxonomiesReducer, {
		isFileChecked: false,
		errors: [],
		data: [],
		providers: [],
		categories: [],
		ageGroups: [],
		selectedProviders: [],
		selectedCategory: "",
		selectedAgeGroups: [],
		fileName: "",
		errorMessages: [],
		courseType: "",
		isSettingAgeGroups: false,
		isDeci: false,
		selectedSemester: "",
		loading: false,
		uploadMode: "NEW" as const,
		selectedCourses: [],
		targetCourses: {},
	});

	// eslint-disable-next-line react/jsx-no-constructed-context-values
	const value = { state, dispatch };
	return (
		<TaxonomiesStateContext.Provider value={value}>
			{children}
		</TaxonomiesStateContext.Provider>
	);
}

function useTaxonomies() {
	const context = React.useContext(TaxonomiesStateContext);
	if (context === undefined) {
		throw new Error("useTaxonomies must be used within a TaxonomiesProvider");
	}
	const { state, dispatch } = context;
	return { state, dispatch };
}

export { useTaxonomies, TaxonomiesProvider };
